import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { useUserStore } from '@/store/user-store';
import { refreshTokenApi } from './auth';
import type { ApiError } from '@/types';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

export const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 10000,
});

// Flag to prevent multiple refresh attempts
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (reason?: unknown) => void;
}> = [];

const processQueue = (error: Error | null, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

/** Clears a hung refresh so a later 401 can try again. Safe if the queue is empty. */
export function resetAuthRefreshState() {
  isRefreshing = false;
  if (failedQueue.length === 0) {
    return;
  }
  processQueue(new Error('Auth refresh reset'), null);
}

function isRefreshAuthFailure(error: unknown): boolean {
  const status = (error as ApiError)?.status;
  return typeof status === 'number' && status >= 400 && status < 500;
}

const redirectToLogin = () => {
  const { clearAuth } = useUserStore.getState();
  clearAuth();

  // Get current path for redirectTo param
  const currentPath = window.location.pathname + window.location.search;

  // Don't add redirectTo if already on auth pages
  if (currentPath.startsWith('/auth/')) {
    window.location.href = '/auth/signin';
    return;
  }

  const redirectTo = encodeURIComponent(currentPath);
  window.location.href = `/auth/signin?redirectTo=${redirectTo}`;
};

// Request interceptor - adds access token from Zustand store
apiClient.interceptors.request.use(
  (config) => {
    const { accessToken } = useUserStore.getState();
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - handles token refresh
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // If 401 and not already retrying
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // Queue the request while refresh is in progress
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return apiClient(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const { refreshToken, setTokens } = useUserStore.getState();

      if (!refreshToken) {
        resetAuthRefreshState();
        redirectToLogin();
        return Promise.reject(error);
      }

      try {
        const response = await refreshTokenApi({ refreshToken });
        const { accessToken: newAccessToken, refreshToken: newRefreshToken } = response;

        // Update tokens in store
        setTokens({
          accessToken: newAccessToken,
          refreshToken: newRefreshToken,
        });

        // Process queued requests
        processQueue(null, newAccessToken);

        // Retry original request
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        return apiClient(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError as Error, null);
        // Invalid refresh token → sign-in. Network/timeout keep the session.
        if (isRefreshAuthFailure(refreshError)) {
          redirectToLogin();
        }
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // Extract error from API response format: { success: false, error: { message, code } }
    const apiError: ApiError = {
      message:
        (error.response?.data as { error?: { message?: string } })?.error?.message ||
        error.message ||
        'An unexpected error occurred',
      code: (error.response?.data as { error?: { code?: string } })?.error?.code,
      status: error.response?.status,
    };

    return Promise.reject(apiError);
  }
);

export default apiClient;
